import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ChatChannelTypeEnum, ChatConversation, ChatNotificationLevelEnum } from 'lib-trend-core';
import { ChatPresenceView, resolvePresenceView } from '../chat-status/chat-status.util';
import { CHAT_FEATURE_KEY, ChatState } from './chat.state';

// Internal Chat store selectors (SPEC-2026-07-internal-chat, T-18).

export const selectChatState = createFeatureSelector<ChatState>(CHAT_FEATURE_KEY);

export const isLoadingSelector = createSelector(selectChatState, (state) => state?.isLoading ?? false);
export const loadErrorSelector = createSelector(selectChatState, (state) => state?.loadError ?? false);
export const bootstrapLoadedSelector = createSelector(selectChatState, (state) => state?.bootstrapLoaded ?? false);

export const conversationsSelector = createSelector(selectChatState, (state) => state?.conversations ?? []);

/** Activity millis — lastMessageAt wins, createdAt is the fallback. */
function activityMillis(conversation: ChatConversation): number {
  const date = conversation.lastMessageAt ?? conversation.createdAt;
  const millis = date ? new Date(date).getTime() : 0;
  return Number.isNaN(millis) ? 0 : millis;
}

/** Conversations sorted by most recent activity (the sidebar/popup order). */
export const sortedConversationsSelector = createSelector(
  conversationsSelector,
  (conversations) => [...conversations].sort((a, b) => activityMillis(b) - activityMillis(a)),
);

export const favoriteSectionsSelector = createSelector(selectChatState, (state) => state?.favoriteSections ?? []);

export const unreadEntriesSelector = createSelector(selectChatState, (state) => state?.unreadCounts ?? {});

/** Back-compat map (channelId → count) consumed by the T-17 shell surfaces. */
export const unreadCountMapSelector = createSelector(
  unreadEntriesSelector,
  (entries) => Object.keys(entries).reduce(
    (counts, channelId) => ({ ...counts, [channelId]: entries[channelId].count }),
    {} as Record<string, number>,
  ),
);

export const totalUnreadSelector = createSelector(
  unreadEntriesSelector,
  (entries) => Object.values(entries).reduce((sum, entry) => sum + Math.max(entry?.count ?? 0, 0), 0),
);

/** Badge label capped at "99+" — what the FAB/topbar pills render. */
export const badgeTextSelector = createSelector(
  totalUnreadSelector,
  (total) => (total > 99 ? '99+' : String(total)),
);

export const presenceSelector = createSelector(selectChatState, (state) => state?.presence ?? {});
export const statusesSelector = createSelector(selectChatState, (state) => state?.statuses ?? {});

/**
 * userId → presence dot + labels of every user the store knows — the single derivation
 * behind a colleague's avatar dot on every surface (conversation list, conversation
 * header, message authors, saved messages, people pickers), so a person reads the same
 * everywhere. No clock of its own: the socket bridge sweeps expired statuses out of
 * the store on its tick (`STATUS_EXPIRY_SWEEP_MS`), which re-runs this selector — so
 * a status can read as live for at most that long past its expiry. A consumer that
 * needs a finer clock keeps its own, like the status button does.
 */
export const presenceViewsSelector = createSelector(
  presenceSelector,
  statusesSelector,
  (presence, statuses) => {
    const views: Record<string, ChatPresenceView> = {};
    new Set([...Object.keys(presence), ...Object.keys(statuses)]).forEach((userId) => {
      views[userId] = resolvePresenceView(statuses[userId], !!presence[userId]);
    });
    return views;
  },
);

/** My most-used reaction emojis, most-used first (RF-11/AC-09). */
export const topEmojisSelector = createSelector(selectChatState, (state) => state?.topEmojis ?? []);
export const mySettingsSelector = createSelector(selectChatState, (state) => state?.mySettings ?? null);

/** Sound preference (T-21 · RF-36) — enabled unless explicitly turned off. */
export const soundEnabledSelector = createSelector(
  mySettingsSelector,
  (settings) => settings?.soundEnabled !== false,
);

/** Quiet-hours window (T-21 · RF-36) — evaluated against the company timezone. */
export const quietHoursSelector = createSelector(
  mySettingsSelector,
  (settings) => ({
    enabled: settings?.quietHoursEnabled === true,
    start: settings?.quietHoursStart ?? '',
    end: settings?.quietHoursEnd ?? '',
  }),
);

/**
 * channelId → my notification level (T-21 · RF-28). Drives the client-side
 * alert gate: conversations I am not a member of never appear here, so the
 * gate falls back to ALL only for conversations I actually belong to.
 */
export const notificationLevelsSelector = createSelector(
  conversationsSelector,
  (conversations) => conversations.reduce((levels, conversation) => ({
    ...levels,
    [conversation._id]: conversation.membership?.notificationLevel ?? ChatNotificationLevelEnum.ALL,
  }), {} as Record<string, ChatNotificationLevelEnum>),
);

/** Ids of my DIRECT conversations — "mentions only" still alerts for DMs (RF-28). */
export const directChannelIdsSelector = createSelector(
  conversationsSelector,
  (conversations) => conversations
    .filter((conversation) => conversation.type === ChatChannelTypeEnum.DIRECT)
    .map((conversation) => conversation._id),
);
export const userDisplaySelector = createSelector(selectChatState, (state) => state?.userDisplay ?? {});

export const savedSelector = createSelector(selectChatState, (state) => state?.saved ?? []);
export const savedLoadingSelector = createSelector(selectChatState, (state) => state?.savedLoading ?? false);

/** Bookmarked message ids (T-20) — drives the message toolbar save/unsave state. */
export const savedIdsSelector = createSelector(selectChatState, (state) => state?.savedIds ?? []);

/** O(1) membership lookup over `savedIds` for the per-message toolbar. */
export const savedIdSetSelector = createSelector(savedIdsSelector, (savedIds) => new Set(savedIds));

/** thread root messageId → reply count already seen (T-20, session-scoped). */
export const threadSeenSelector = createSelector(selectChatState, (state) => state?.threadSeen ?? {});
export const discoveredSelector = createSelector(selectChatState, (state) => state?.discovered ?? []);
export const discoverLoadingSelector = createSelector(selectChatState, (state) => state?.discoverLoading ?? false);
/** Pagination meta of the public-channel directory (T-35 · RF-03). */
export const discoverMetaSelector = createSelector(selectChatState, (state) => state?.discoverMeta ?? null);

export const activeChannelIdSelector = createSelector(selectChatState, (state) => state?.activeChannelId ?? null);
export const directDraftSelector = createSelector(selectChatState, (state) => state?.directDraft ?? null);

export const activeConversationSelector = createSelector(
  conversationsSelector,
  activeChannelIdSelector,
  (conversations, activeChannelId) =>
    (activeChannelId ? conversations.find((conversation) => conversation._id === activeChannelId) ?? null : null),
);

/** conversationKey → composer draft (T-19 per-conversation draft persistence). */
export const composerDraftsSelector = createSelector(selectChatState, (state) => state?.composerDrafts ?? {});

// interface states (T-25 · RNF-08) --------------------------------------------

/** True as soon as the snapshot carries at least one conversation. */
export const hasConversationsSelector = createSelector(
  conversationsSelector,
  (conversations) => conversations.length > 0,
);

/** Websocket link state — the connection banner reads only this + `restored`. */
export const connectionStatusSelector = createSelector(
  selectChatState,
  (state) => state?.connectionStatus ?? 'connecting',
);

/** Brief post-recovery flag (auto-cleared by ChatConnectionService). */
export const connectionRestoredSelector = createSelector(
  selectChatState,
  (state) => state?.connectionRestored ?? false,
);

export const bootstrapErrorKindSelector = createSelector(
  selectChatState,
  (state) => state?.bootstrapErrorKind ?? null,
);

/**
 * Chat backend unreachable AND nothing to show → the degraded "chat
 * unavailable" surface. Deliberately requires `!bootstrapLoaded`: a refresh
 * that fails while a snapshot is already on screen keeps the conversations
 * usable (the connection banner carries the warning instead).
 */
export const chatUnavailableSelector = createSelector(
  bootstrapLoadedSelector,
  bootstrapErrorKindSelector,
  (loaded, kind) => !loaded && kind === 'unavailable',
);

/** Backend answered but refused/failed AND nothing to show → error + retry. */
export const bootstrapFailedSelector = createSelector(
  bootstrapLoadedSelector,
  bootstrapErrorKindSelector,
  (loaded, kind) => !loaded && kind === 'error',
);

/** First snapshot still pending (no data, no failure yet) → skeletons. */
export const bootstrapPendingSelector = createSelector(
  bootstrapLoadedSelector,
  bootstrapErrorKindSelector,
  (loaded, kind) => !loaded && !kind,
);

export const errorSelector = createSelector(selectChatState, (state) => state?.error ?? null);
